import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'

import { TableCellsIcon } from '@gto/icons-react/outline/table-cells'
import type { PaginationState } from '@gto/ui/data-table'

import { useGetMyCommissions } from '@/api/gen'
import { CommissionsTable } from '@/components/commissions-table'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { TableFilterBar } from '@/components/table-filter-bar'
import { useReportQueryError } from '@/hooks/use-report-query-error'

const FILTER_OPTION_KEYS = [
  { value: 'ALL', labelId: 'commissions.filter.all' },
  { value: 'LOCKED', labelId: 'commissions.filter.locked' },
  { value: 'UNLOCKED', labelId: 'commissions.filter.unlocked' },
  { value: 'PAID', labelId: 'commissions.filter.paid' },
  { value: 'REFUNDED', labelId: 'commissions.filter.refunded' },
] as const

export const CommissionsPage = () => {
  const { formatMessage } = useIntl()
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [paginationModel, setPaginationModel] = useState<PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  })

  const activeStatus = statusFilter === 'ALL' ? undefined : statusFilter

  const commissionsQuery = useGetMyCommissions(
    {
      ...(activeStatus == null ? {} : { status: activeStatus }),
      limit: paginationModel.pageSize,
      offset: paginationModel.pageIndex * paginationModel.pageSize,
    },
    { query: { placeholderData: keepPreviousData } },
  )
  const { data, isLoading, isError, refetch } = commissionsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(commissionsQuery, 'getMyCommissions failed: ')

  const rows = data?.items ?? []
  const rowCount = data?.total ?? 0

  const filterOptions = FILTER_OPTION_KEYS.map((opt) => ({
    value: opt.value,
    label: formatMessage({ id: opt.labelId }),
  }))

  const handleFilterChange = (value: string) => {
    setStatusFilter(value)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="commissions.title" />} />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="commissions-error"
            messageId="commissions.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <TableFilterBar
              options={filterOptions}
              value={statusFilter}
              onValueChange={handleFilterChange}
            />

            <StatsCard
              icon={<TableCellsIcon />}
              title={<FormattedMessage id="commissions.listTitle" />}
            >
              {!isLoading && rows.length === 0 ? (
                <EmptyState title={<FormattedMessage id="commissions.empty" />} />
              ) : (
                <CommissionsTable
                  manualPagination
                  data={rows}
                  getRowId={(row) => row.id}
                  loading={isLoading}
                  pagination={paginationModel}
                  rowCount={rowCount}
                  onPaginationChange={setPaginationModel}
                />
              )}
            </StatsCard>
          </>
        )}
      </Page.Content>
    </Page>
  )
}
